import {
  getBomData,
  getBoms,
  getBomsSelection,
  getPeople,
  getPeopleSelection,
  setPeopleRoles,
} from "../slice/productionSlice";
import { apiSlice } from "./apiSlice";

export const productionApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getPeople: builder.query({
      query: (params = null) => {
        const queryParams = params ? `?${params}` : "";
        return `/api/production/v1/people/${queryParams}`;
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getPeople(result.data))).catch(() => {});
      },
    }),
    getPeopleSelection: builder.query({
      query: args => ({
        url: "/api/production/v1/people/selection/",
        params: args?.params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getPeopleSelection(result.data))).catch(() => {});
      },
    }),
    getPersonById: builder.query({
      query: id => `/api/production/v1/people/${id}/`,
    }),
    createPerson: builder.mutation({
      query: data => ({
        url: "/api/production/v1/people/",
        method: "POST",
        body: data,
      }),
    }),
    updatePerson: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/people/${id}/`,
        method: "PUT",
        body: data,
      }),
    }),
    patchPerson: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/people/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    deletePerson: builder.mutation({
      query: id => ({
        url: `/api/production/v1/people/${id}/`,
        method: "DELETE",
      }),
    }),
    getPeopleRoles: builder.query({
      query: () => "/api/production/v1/people/roles/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(setPeopleRoles(result.data))).catch(() => {});
      },
    }),
    getBoms: builder.query({
      query: (params = null) => {
        const queryParams = params ? `?${params}` : "";
        return `/api/production/v1/boms/${queryParams}`;
      },
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getBoms(result.data))).catch(() => {});
      },
      providesTags: ["bomList"],
    }),
    getBomsSelection: builder.query({
      query: ({ params }) => ({
        url: `/api/production/v1/boms/selection/`,
        params,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getBomsSelection(result.data))).catch(() => {});
      },
    }),
    getBomDetail: builder.query({
      query: id => `/api/production/v1/boms/${id}/`,
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getBomData(result.data))).catch(() => {});
      },
      providesTags: (result, error, args) => [
        { type: "bomDetail", id: typeof args === "object" ? args.id : args },
      ],
    }),
    createBom: builder.mutation({
      query: data => ({
        url: "/api/production/v1/boms/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["bomList"],
    }),
    updateBom: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/boms/${id}/`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["bomList", { type: "bomDetail", id }],
    }),
    patchBom: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/production/v1/boms/${id}/`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => ["bomList", { type: "bomDetail", id }],
    }),
    deleteBom: builder.mutation({
      query: id => ({
        url: `/api/production/v1/boms/${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => ["bomList", { type: "bomDetail", id }],
    }),
    getBomSteps: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/bom_steps/?${queryParams}`;
      },
    }),
    getBomComponents: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/bom_components/?${queryParams}`;
      },
    }),
    getBomLabours: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/bom_labours/?${queryParams}`;
      },
    }),
    getBomServices: builder.query({
      query: params => {
        const queryParams = new URLSearchParams(params).toString();
        return `/api/production/v1/bom_services/?${queryParams}`;
      },
    }),
    getBomHistory: builder.query({
      query: ({ id, limit = 20, offset = 0 }) => ({
        url: `/api/production/v1/boms/${id}/history/`,
        params: { limit, offset },
      }),
    }),
    createOrderlessManufacturingEntry: builder.mutation({
      query: data => ({
        url: "/api/production/v1/orderless_manufacturing/",
        method: "POST",
        body: data,
      }),
    }),
  }),
});

export const {
  useGetPeopleQuery,
  useLazyGetPeopleQuery,
  useGetPeopleSelectionQuery,
  useLazyGetPeopleSelectionQuery,
  useGetPersonByIdQuery,
  useCreatePersonMutation,
  usePatchPersonMutation,
  useDeletePersonMutation,
  useGetPeopleRolesQuery,
  useLazyGetPeopleRolesQuery,
  useGetBomsQuery,
  useLazyGetBomsQuery,
  useGetBomsSelectionQuery,
  useLazyGetBomsSelectionQuery,
  useGetBomDetailQuery,
  useCreateBomMutation,
  useUpdateBomMutation,
  usePatchBomMutation,
  useDeleteBomMutation,
  useGetBomStepsQuery,
  useLazyGetBomStepsQuery,
  useGetBomComponentsQuery,
  useLazyGetBomComponentsQuery,
  useGetBomLaboursQuery,
  useLazyGetBomLaboursQuery,
  useGetBomServicesQuery,
  useLazyGetBomServicesQuery,
  useGetBomHistoryQuery,
  useLazyGetBomHistoryQuery,
  useCreateOrderlessManufacturingEntryMutation,
} = productionApiSlice;
